import CloseIcon from '@mui/icons-material/Close';
import { Button, Dialog, Typography } from '@mui/material';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';

import alert from '~/assets/images/alert.svg';
import { useDocumentVisibility } from '~/hooks/useDocumentVisibility';
import { useUpdateEffect } from '~/hooks/useUpdateEffect';
import { useUserActivity } from '~/hooks/useUserActivity';
import { useLockCompanyMutation } from '~/services/rtkQuery/company';
import { getApiError } from '~/utils/helpers';

import { EditingBanner } from '../EditingBanner';
import styles from './styles.module.scss';

const EDITING_EXPIRE_TIME = 900000; // 15 minutes
const EDITING_AUTO_EXTEND_INTERVAL = 60000; // 1 minute
const AUTOSAVE_EXPIRE_TIME = 60000; // 1 minute

interface Props {
  companyId: number | null;
  companyLocked: boolean;
  loadingSaveAsDraft: boolean;
  onLoading: (loading: boolean) => void;
  onCompanyLocked: (locked: boolean) => void;
  onSaveAsDraft: () => Promise<void>;
}

const Component: React.FC<Props> = ({
  companyId,
  companyLocked,
  loadingSaveAsDraft,
  onLoading,
  onCompanyLocked,
  onSaveAsDraft,
}) => {
  const [lockedMessage, setLockedMessage] = useState<string | null>(null);
  const [editingBannerVisible, setEditingBannerVisible] = useState(false);
  const [extendModalVisible, setExtendModalVisible] = useState(false);
  const lockCompanyInitialized = useRef(false);
  const editingTimerId = useRef<NodeJS.Timeout | null>(null);
  const editingTimerStartDate = useRef<number | null>(null);
  const { isUserActive } = useUserActivity();
  const { isDocumentHidden } = useDocumentVisibility();
  const [lockCompanyMutation, { isLoading: isLoadingLockCompanyMutation }] =
    useLockCompanyMutation();

  const showEditingBanner = useCallback(() => {
    setEditingBannerVisible(true);
  }, []);

  const hideEditingBanner = useCallback(() => {
    setEditingBannerVisible(false);
  }, []);

  const showExtendModal = useCallback(() => {
    setExtendModalVisible(true);
  }, []);

  const hideExtendModal = useCallback(() => {
    setExtendModalVisible(false);
  }, []);

  const startEditingTimer = useCallback(() => {
    if (editingTimerId.current) {
      clearTimeout(editingTimerId.current);
    }

    editingTimerStartDate.current = Date.now();

    editingTimerId.current = setTimeout(showExtendModal, EDITING_EXPIRE_TIME);
  }, [showExtendModal]);

  const lockCompany = useCallback(async () => {
    try {
      if (companyId) {
        hideExtendModal();

        await lockCompanyMutation({ id: companyId }).unwrap();

        startEditingTimer();
      }
    } catch (e) {
      const apiError = getApiError(e);

      if (apiError && 'message' in apiError.data) {
        setLockedMessage(apiError.data.message);

        onCompanyLocked(true);
      }
    } finally {
      if (companyId) {
        lockCompanyInitialized.current = true;

        showEditingBanner();
      }
    }
  }, [
    companyId,
    hideExtendModal,
    lockCompanyMutation,
    startEditingTimer,
    onCompanyLocked,
    showEditingBanner,
  ]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (isUserActive && !extendModalVisible) {
      lockCompany();

      const intervalId = setInterval(lockCompany, EDITING_AUTO_EXTEND_INTERVAL);

      return () => clearInterval(intervalId);
    }
  }, [extendModalVisible, isUserActive, lockCompany]);

  useUpdateEffect(() => {
    onLoading(!lockCompanyInitialized.current && isLoadingLockCompanyMutation);
  }, [isLoadingLockCompanyMutation]);

  const handleSaveAndClose = useCallback(async () => {
    if (!loadingSaveAsDraft) {
      await onSaveAsDraft();
    }
  }, [loadingSaveAsDraft, onSaveAsDraft]);

  useEffect(() => {
    if (!isDocumentHidden) {
      if (
        editingTimerStartDate.current &&
        Date.now() - editingTimerStartDate.current > EDITING_EXPIRE_TIME
      ) {
        handleSaveAndClose();
      }
    }
  }, [handleSaveAndClose, isDocumentHidden]);

  useUpdateEffect(() => {
    if (extendModalVisible) {
      const timeoutId = setTimeout(handleSaveAndClose, AUTOSAVE_EXPIRE_TIME);

      return () => clearTimeout(timeoutId);
    }

    return undefined;
  }, [extendModalVisible]);

  return (
    <>
      <EditingBanner
        text={companyLocked ? (lockedMessage as string) : 'Editing mode is on'}
        visible={editingBannerVisible}
        onClose={hideEditingBanner}
      />
      <Dialog open={extendModalVisible} onClose={hideExtendModal}>
        <div className={styles['extend-modal']}>
          <button
            aria-label="close"
            className={styles['extend-modal__close']}
            type="button"
            onClick={hideExtendModal}
          >
            <CloseIcon sx={{ width: '18px', height: '18px', color: 'var(--gray-2-color)' }} />
          </button>
          <Typography color="text.primary" mb="32px" variant="h5">
            Inactive time limit
          </Typography>
          <div className={styles['extend-modal__icon-wrapper']}>
            <img alt="Alert" src={alert} />
          </div>
          <Typography color="var(--text-3-color)" mb="24px" variant="subtitle1">
            Time limit for editing has expired. Continue editing or close the page?
          </Typography>
          <div className={styles['extend-modal__buttons']}>
            <Button
              color="secondary"
              loading={loadingSaveAsDraft}
              sx={{ width: '160px', color: 'var(--text-3-color)' }}
              onClick={handleSaveAndClose}
            >
              Save & Close
            </Button>
            <Button disabled={loadingSaveAsDraft} sx={{ width: '160px' }} onClick={hideExtendModal}>
              Continue
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export const ConcurrentMode = memo(Component);
